import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Button, IconButton,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Alert, Tooltip,
  useMediaQuery, useTheme, Stack, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Block as VoidIcon,
  Refresh as RefreshIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { serverMessage } from '../../utils/apiConvert';
import ReceiptService from '../../services/receipt.service';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { can } from '../../utils/roleUtils';
import { ProTableStats } from '../../components/pro-table';

const TOOLBAR_SX = {
  '& .MuiInputBase-root': { height: 32 },
  '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
};

const fontSize = {
  xs: '0.75rem',
  sm: '0.8125rem',
  md: '0.875rem',
};

const Receipts = ({ currentUser }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    paymentMethod: '',
    source: '',
    search: '',
    dateFrom: '',
    dateTo: ''
  });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Void dialog
  const [voidDialog, setVoidDialog] = useState({ open: false, receipt: null });
  const [voidReason, setVoidReason] = useState('');
  const [voidSubmitting, setVoidSubmitting] = useState(false);

  const typingCenterId = currentUser?.typingCenterId;

  const fetchData = useCallback(async () => {
    if (!typingCenterId) return;
    setLoading(true);
    setError(null);
    try {
      const [receiptsResult, statsResult] = await Promise.all([
        ReceiptService.getAll({ ...filters, typingCenterId }),
        ReceiptService.getStatistics(typingCenterId)
      ]);
      setReceipts(receiptsResult.data);
      setTotalCount(receiptsResult.total);
      setStatistics(statsResult);
    } catch (err) {
      console.error('Error fetching receipts:', err);
      setError('Failed to load receipts');
    } finally {
      setLoading(false);
    }
  }, [filters, typingCenterId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

  const paginatedReceipts = receipts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Void handlers
  const openVoidDialog = (receipt) => {
    setVoidDialog({ open: true, receipt });
    setVoidReason('');
  };

  const closeVoidDialog = () => {
    setVoidDialog({ open: false, receipt: null });
    setVoidReason('');
  };

  const handleVoidSubmit = async () => {
    if (!voidDialog.receipt || !voidReason.trim()) return;
    setVoidSubmitting(true);
    try {
      await ReceiptService.void(voidDialog.receipt.id, voidReason);
      closeVoidDialog();
      fetchData();
    } catch (err) {
      setError(serverMessage(err, 'Failed to void receipt'));
      closeVoidDialog();
      fetchData();
    } finally {
      setVoidSubmitting(false);
    }
  };

  // Summary Cards
  const renderSummaryCards = () => (
    <ProTableStats
      loading={!statistics}
      cards={[
        { label: 'Total Received', value: ReceiptService.formatCurrency(statistics?.totalReceived || 0), color: 'success.main' },
        { label: 'Received MTD', value: ReceiptService.formatCurrency(statistics?.receivedMtd || 0), color: 'info.main' },
        { label: 'Receipts', value: statistics?.receiptCount ?? 0 },
        { label: 'Voided Amount', value: ReceiptService.formatCurrency(statistics?.voidedAmount || 0), color: (statistics?.voidedAmount || 0) > 0 ? 'error.main' : undefined },
      ]}
    />
  );

  // Filters
  const renderFilters = () => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5, ...TOOLBAR_SX }}>
          <TextField size="small" placeholder={isMobile ? "Search..." : "Search receipt #, customer, invoice..."}
            sx={{ flex: '1 1 200px', minWidth: 200 }}
            value={filters.search} onChange={(e) => { setFilters(prev => ({ ...prev, search: e.target.value })); setPage(0); }}
            onKeyPress={(e) => e.key === 'Enter' && fetchData()}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment> }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filters.status} label="Status" onChange={(e) => { setFilters(prev => ({ ...prev, status: e.target.value })); setPage(0); }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="VOIDED">Voided</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Payment Method</InputLabel>
            <Select value={filters.paymentMethod} label="Payment Method" onChange={(e) => { setFilters(prev => ({ ...prev, paymentMethod: e.target.value })); setPage(0); }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="WALLET">Wallet</MenuItem>
              <MenuItem value="CASH">Cash</MenuItem>
              <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
              <MenuItem value="CHEQUE">Cheque</MenuItem>
              <MenuItem value="ONLINE">Online</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Source</InputLabel>
            <Select value={filters.source} label="Source" onChange={(e) => { setFilters(prev => ({ ...prev, source: e.target.value })); setPage(0); }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="MANUAL">Manual</MenuItem>
              <MenuItem value="SETTLEMENT">Settlement</MenuItem>
              <MenuItem value="ONLINE_PAYMENT">Online Payment</MenuItem>
              <MenuItem value="CHEQUE_CLEARED">Cheque Cleared</MenuItem>
              <MenuItem value="TOPUP_APPROVED">Top-up Approved</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} sx={{ width: 150 }}
            value={filters.dateFrom} onChange={(e) => { setFilters(prev => ({ ...prev, dateFrom: e.target.value })); setPage(0); }}
          />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} sx={{ width: 150 }}
            value={filters.dateTo} onChange={(e) => { setFilters(prev => ({ ...prev, dateTo: e.target.value })); setPage(0); }}
          />
          <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', whiteSpace: 'nowrap', mr: 1 }}>
              {totalCount} receipt{totalCount !== 1 ? 's' : ''}
            </Typography>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={fetchData} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
    </Box>
  );

  return (
    <Box sx={{ pb: isMobile ? 2 : 0, width: 0, minWidth: '100%' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {renderSummaryCards()}
      {renderFilters()}

      {/* Mobile Cards / Desktop Table */}
      {isMobile ? (
        <Stack spacing={1.5}>
          {loading ? <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box> :
          paginatedReceipts.length === 0 ? <Paper sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No receipts found</Typography></Paper> :
          paginatedReceipts.map((receipt) => (
            <Card key={receipt.id} variant="outlined" sx={{ cursor: 'pointer', opacity: receipt.status === 'VOIDED' ? 0.7 : 1 }} onClick={() => navigate(`/receipts/${receipt.id}`)}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                  <Box>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: 'secondary.main', textDecoration: receipt.status === 'VOIDED' ? 'line-through' : 'none' }}>
                      {receipt.receiptNumber}
                    </Typography>
                    <Typography sx={{ fontSize: fontSize.xs }}>{receipt.customerName}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Chip label={ReceiptService.getStatusLabel(receipt.status)} size="small" color={ReceiptService.getStatusColor(receipt.status)} sx={{ fontSize: fontSize.xs, height: 22 }} />
                    <ChevronRightIcon color="action" />
                  </Box>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography sx={{ fontSize: fontSize.xs }}>{receipt.invoiceNumber} - {formatDate(receipt.date)}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: receipt.status === 'VOIDED' ? 'text.secondary' : (isDark ? '#4caf50' : '#2e7d32'), textDecoration: receipt.status === 'VOIDED' ? 'line-through' : 'none' }}>
                    {ReceiptService.formatCurrency(receipt.amountApplied)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead sx={getBrandedTableHeadSx(theme)}>
                <TableRow>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Receipt #</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Invoice #</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Invoice Amount</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Amount Applied</TableCell>
                  <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Payment Method</TableCell>
                  <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Source</TableCell>
                  <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : paginatedReceipts.length === 0 ? (
                  <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4 }}><Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No receipts found</Typography></TableCell></TableRow>
                ) : paginatedReceipts.map((receipt) => {
                  const isVoided = receipt.status === 'VOIDED';
                  return (
                    <TableRow key={receipt.id} hover sx={{ opacity: isVoided ? 0.7 : 1 }}>
                      <TableCell sx={{ py: 1 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'secondary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => navigate(`/receipts/${receipt.id}`)}>
                          {receipt.receiptNumber}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '13px', py: 1, color: isVoided ? 'text.secondary' : 'text.primary' }}>
                        {formatDate(receipt.date)}
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        {receipt.customerName && receipt.internalOrganizationId ? (
                          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'secondary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            onClick={() => navigate(`/organizations/internal/${receipt.internalOrganizationId}`)}>
                            {receipt.customerName}
                          </Typography>
                        ) : (
                          <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{receipt.customerName || '—'}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'secondary.main', cursor: 'pointer' }}
                          onClick={() => navigate(`/invoices/${receipt.invoiceId}`)}>
                          {receipt.invoiceNumber}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>
                        <Typography sx={{ fontSize: '13px', color: isVoided ? 'text.secondary' : 'text.primary' }}>
                          {ReceiptService.formatCurrency(receipt.invoiceAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: isVoided ? 'text.secondary' : (isDark ? '#4caf50' : '#2e7d32'), textDecoration: isVoided ? 'line-through' : 'none' }}>
                          {ReceiptService.formatCurrency(receipt.amountApplied)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        {receipt.paymentMethod ? (
                          <Chip label={ReceiptService.getPaymentMethodLabel(receipt.paymentMethod)} size="small" variant="outlined"
                            color={ReceiptService.getPaymentMethodColor(receipt.paymentMethod)} sx={{ fontSize: fontSize.xs, height: 22 }} />
                        ) : <Typography component="span" sx={{ fontSize: '13px', color: 'text.disabled' }}>—</Typography>}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        {receipt.initiator ? (
                          <Chip label={ReceiptService.getInitiatorLabel(receipt.initiator)} size="small"
                            color={ReceiptService.getInitiatorColor(receipt.initiator)} sx={{ fontSize: fontSize.xs, height: 22 }} />
                        ) : <Typography component="span" sx={{ fontSize: '13px', color: 'text.disabled' }}>—</Typography>}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        <Chip label={ReceiptService.getStatusLabel(receipt.status)} size="small"
                          color={ReceiptService.getStatusColor(receipt.status)} sx={{ fontSize: fontSize.xs, height: 22 }} />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        <Tooltip title="View Receipt">
                          <IconButton size="small" onClick={() => navigate(`/receipts/${receipt.id}`)}>
                            <ViewIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        {receipt.status === 'COMPLETED' && can(currentUser, 'finance', 'write') && (
                          <Tooltip title="Void Receipt">
                            <IconButton size="small" color="error" onClick={() => openVoidDialog(receipt)}>
                              <VoidIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={totalCount} page={page}
            onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      )}

      {/* Mobile Pagination */}
      {isMobile && receipts.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <TablePagination component="div" count={totalCount} page={page}
            onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Per page:"
          />
        </Box>
      )}

      {/* Void Receipt Dialog */}
      <Dialog open={voidDialog.open} onClose={closeVoidDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: fontSize.md, fontWeight: 600, pb: 1 }}>
          Void Receipt
        </DialogTitle>
        <DialogContent>
          {voidDialog.receipt && (
            <Box>
              <Box sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#f5f5f5', borderRadius: 1, p: 2, mb: 2 }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: fontSize.xs }}>Receipt Number</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{voidDialog.receipt.receiptNumber}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: fontSize.xs }}>Invoice</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{voidDialog.receipt.invoiceNumber}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: fontSize.xs }}>Amount Applied</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: isDark ? '#4caf50' : '#2e7d32' }}>
                      {ReceiptService.formatCurrency(voidDialog.receipt.amountApplied)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: fontSize.xs }}>Customer</Typography>
                    <Typography sx={{ fontSize: fontSize.sm }}>{voidDialog.receipt.customerName}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Alert severity="warning" sx={{ mb: 2, fontSize: fontSize.sm }}>
                This will refund <strong>{ReceiptService.formatCurrency(voidDialog.receipt.amountApplied)}</strong> back to the customer wallet and update the invoice status.
              </Alert>

              <TextField fullWidth size="small" label="Void Reason" multiline rows={3}
                value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
                required placeholder="Enter reason for voiding this receipt..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeVoidDialog} size="small" sx={{ fontSize: fontSize.sm }}>Cancel</Button>
          <Button variant="contained" color="error" size="small"
            onClick={handleVoidSubmit}
            disabled={voidSubmitting || !voidReason.trim()}
            sx={{ fontSize: fontSize.sm }}>
            {voidSubmitting ? <CircularProgress size={18} color="inherit" /> : 'Confirm Void'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Receipts;
